import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/articleData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const displayCategory = currentCategory || categories[0];
  const displayArticles = currentCategory ? categoryArticles : articles;
  const pageSlug = slug || categories[0].slug;

  const jsonLdCollection = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${displayCategory.name} - 17.c 起草官网`,
    "description": displayCategory.description,
    "url": `https://17.c/category/${pageSlug}`,
    "isPartOf": { "@type": "WebSite", "name": "17.c 起草官网", "url": "https://17.c" }
  });

  const jsonLdBreadcrumb = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://17.c" },
      { "@type": "ListItem", "position": 2, "name": displayCategory.name, "item": `https://17.c/category/${pageSlug}` }
    ]
  });

  return (
    <>
      <title>{displayCategory.name} - 17.c 起草官网</title>
      <meta name="description" content={displayCategory.description} />
      <link rel="canonical" href={`https://17.c/category/${pageSlug}`} />
      <script type="application/ld+json">{jsonLdCollection}</script>
      <script type="application/ld+json">{jsonLdBreadcrumb}</script>

      <div className="min-h-screen bg-background-50">
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: displayCategory.name }]} />

            <div className="mb-10">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${displayCategory.icon} text-2xl text-primary-600`} />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                    {displayCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {displayCategory.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-10">
              <Link
                to="/category/wenjian-qicao"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  (!slug || slug === 'wenjian-qicao')
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 pb-16">
              {displayArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.listImageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}